import { useNavigate } from 'react-router-dom';
import { useCurrentUser } from '@api/queries';
import {
  availableRoles,
  activeRole,
  setActiveRole,
  roleHome,
  ROLE_LABEL,
} from '@auth/roles';
import styles from './RoleSwitcher.module.css';

/**
 * RoleSwitcher (B144)
 *
 * A compact segmented control shown in the Clinic Admin and Doctor headers for
 * a login that holds BOTH roles (a doctor who is also this clinic's admin).
 * Renders nothing for everyone else, so it is safe to drop into any shell.
 *
 * Switching sets the active-role flag and navigates to that role's home. The
 * server already accepts the same token for both apps (doctor endpoints key
 * off the JWT profile, not the role), so no re-auth is needed.
 */
export default function RoleSwitcher() {
  const { data: user } = useCurrentUser();
  const navigate = useNavigate();

  const roles = availableRoles(user);
  if (roles.length < 2) return null;

  const active = activeRole(user);

  const pick = (role) => {
    if (role === active) return;
    setActiveRole(role);
    navigate(roleHome(role));
  };

  return (
    <div className={styles.switcher} role="group" aria-label="Switch role">
      {roles.map((r) => (
        <button
          key={r}
          type="button"
          className={`${styles.seg} ${styles[r] || ''} ${r === active ? styles.active : ''}`}
          onClick={() => pick(r)}
          aria-pressed={r === active}
          title={r === active ? `You are in the ${ROLE_LABEL[r]} view` : `Switch to ${ROLE_LABEL[r]} view`}
        >
          {ROLE_LABEL[r] || r}
        </button>
      ))}
    </div>
  );
}
